/**
 * App root: JWT login gate + main dashboard.
 *
 * Flow (for beginners):
 * 1. On load, if `xspectre_access_token` exists in localStorage, we show `DashboardPage`.
 * 2. Otherwise we show a login form; `loginWithPassword()` in `api.js` POSTs to `/api/v1/auth/token/`
 *    and stores tokens.
 * 3. Logout clears tokens via `logoutUser()` and returns to the login screen.
 *
 * Working without a backend (local UI only):
 * - Set `REACT_APP_SKIP_LOGIN=true` in `.env.development.local` to open the dashboard immediately (no JWT).
 * - API-driven widgets will use fallbacks or show empty/error states until the API is reachable.
 *
 * Routing: `/app/*` uses nested route components that render dashboard sections.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import ButtonBusyLabel from './components/ButtonBusyLabel';
import LoginSignupDialog from './components/LoginSignupDialog';
import DashboardLayout from './layouts/DashboardLayout';
import MfaOtpCodeSlots, { MFA_OTP_SLOT_COUNT } from './shared/components/MfaOtpCodeSlots';
import { API_ROOT_URL, ensureAuthenticatedSession, getEffectiveApiBaseDescription, hasUsableAccessToken, clearLoginTime, getLoginTime, subscribeToAuthExpired, isForcePasswordChangePending, setForcePasswordChangePending, IDLE_TIMEOUT_MS, IDLE_WARN_BEFORE_MS, SESSION_MAX_MS } from './services/_core';
import { loginWithPassword, loginWithSsoCode, verifyLoginMfaCode, resendLoginMfaCode, logoutUser, requestPasswordReset, validatePasswordResetKey, confirmPasswordReset, acceptInvitation, validateInvitationKey, forcePasswordChange } from './services/auth.service';
import { clearAuthenticatedQueryCache } from './queryClient';
import { clearMfaEnrollmentPromptSession } from './features/settings/profile/MfaEnrollmentPrompt';
import useIdleTimeout from './hooks/useIdleTimeout';
import {
  TOUR_AUTH_LOGOUT_EVENT,
  forceRemoveDriverDom,
} from './features/onboarding/tourLifecycle';

const SKIP_LOGIN = process.env.REACT_APP_SKIP_LOGIN === 'true';

const apiBaseForErrors = getEffectiveApiBaseDescription();

function loginPublicAsset(filename) {
  const base = process.env.PUBLIC_URL || '';
  return base ? `${base.replace(/\/$/, '')}/${filename}` : `/${filename}`;
}

const iconStroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' };

function IconEnvelope() {
  return (
    <svg className="login-field-svg" viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
      <path
        {...iconStroke}
        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
      />
    </svg>
  );
}

function IconLock() {
  return (
    <svg className="login-field-svg" viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
      <path
        {...iconStroke}
        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
      />
    </svg>
  );
}

function IconEye() {
  return (
    <svg className="login-field-svg" viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
      <path {...iconStroke} d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12z" />
      <path {...iconStroke} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg className="login-field-svg" viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
      <path
        {...iconStroke}
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  );
}

function IconGoogle() {
  return (
    <svg className="login-sso-svg" viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
      <path
        fill="#EA4335"
        d="M5.27 14.09L2.1 16.59C3.69 19.9 7.56 22 12 22c3.24 0 5.95-1.08 7.93-2.91l-3.47-2.77c-1.04.7-2.37 1.12-4.02 1.12-3.09 0-5.71-2.09-6.65-4.91z"
      />
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      />
      <path
        fill="#34A853"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function IconMicrosoft() {
  return (
    <svg className="login-sso-svg" viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
      <path fill="#f25022" d="M1 1h10v10H1z" />
      <path fill="#7fba00" d="M13 1h10v10H13z" />
      <path fill="#00a4ef" d="M1 13h10v10H1z" />
      <path fill="#ffb900" d="M13 13h10v10H13z" />
    </svg>
  );
}

function IconGithub() {
  return (
    <svg className="login-sso-svg" viewBox="0 0 24 24" width={22} height={22} aria-hidden="true" fill="#e6edf3">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.89 1.52 2.341 1.08 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.94 0-1.09.39-1.98 1.029-2.675-.103-.253-.446-1.27.098-2.65 0 0 .84-.27 2.75 1.026A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.695 1.028 1.584 1.028 2.675 0 3.838-2.339 4.685-4.566 4.934.359.309.678.919.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.481C19.138 20.167 22 16.42 22 12c0-5.523-4.477-10-10-10z"
      />
    </svg>
  );
}

function formatCooldownLabel(seconds) {
  const value = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(value / 60);
  const secs = value % 60;
  if (mins > 0) return `${mins}:${String(secs).padStart(2, '0')}`;
  return `${secs}s`;
}

function LoginForgotPasswordDialog({
  open,
  initialEmail,
  onClose,
}) {
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPhase, setForgotPhase] = useState('form');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotCooldownSeconds, setForgotCooldownSeconds] = useState(0);
  const emailInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setForgotEmail(String(initialEmail || '').trim());
    setForgotPhase('form');
    setForgotMessage('');
    setForgotError('');
    setForgotSubmitting(false);
    setForgotCooldownSeconds(0);
    const t = window.setTimeout(() => emailInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, initialEmail]);

  useEffect(() => {
    if (!open || forgotCooldownSeconds <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      setForgotCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [open, forgotCooldownSeconds]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !forgotSubmitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, forgotSubmitting, onClose]);

  if (!open) return null;

  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget && !forgotSubmitting) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (forgotSubmitting || forgotCooldownSeconds > 0) return;
    setForgotError('');
    setForgotSubmitting(true);
    try {
      const result = await requestPasswordReset(forgotEmail);
      const resendCooldown = Number(result?.resendCooldownSeconds || result?.retryAfterSeconds || 0);
      setForgotMessage(
        String(
          result?.message ||
            'If an account exists for that email address, password reset instructions have been sent.'
        )
      );
      setForgotCooldownSeconds(
        Number.isFinite(resendCooldown) && resendCooldown > 0 ? Math.ceil(resendCooldown) : 0
      );
      setForgotPhase('done');
    } catch (err) {
      const retryAfterSeconds = Number(err?.retryAfterSeconds || 0);
      if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        setForgotCooldownSeconds(Math.ceil(retryAfterSeconds));
      }
      setForgotError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'forgot_password' }));
    } finally {
      setForgotSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (forgotSubmitting || forgotCooldownSeconds > 0) return;
    setForgotError('');
    setForgotSubmitting(true);
    try {
      const result = await requestPasswordReset(forgotEmail);
      const resendCooldown = Number(result?.resendCooldownSeconds || result?.retryAfterSeconds || 0);
      setForgotMessage(
        String(
          result?.message ||
            'If an account exists for that email address, password reset instructions have been sent.'
        )
      );
      setForgotCooldownSeconds(
        Number.isFinite(resendCooldown) && resendCooldown > 0 ? Math.ceil(resendCooldown) : 0
      );
      setForgotPhase('done');
    } catch (err) {
      const retryAfterSeconds = Number(err?.retryAfterSeconds || 0);
      if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        setForgotCooldownSeconds(Math.ceil(retryAfterSeconds));
      }
      setForgotError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'forgot_password' }));
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div
      className="login-forgot-overlay"
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        className="login-forgot-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-forgot-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="login-forgot-dialog-head">
          <h2 id="login-forgot-title" className="login-forgot-title">
            Forgot password
          </h2>
          <button
            type="button"
            className="login-forgot-close"
            onClick={onClose}
            disabled={forgotSubmitting}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {forgotPhase === 'done' ? (
          <div className="login-forgot-body">
            <p className="login-forgot-success">{forgotMessage}</p>
            <p className="login-forgot-hint">
              Check your inbox and spam folder. Follow the link in the email to choose a new password.
            </p>
            {forgotError ? (
              <div id="login-forgot-error" className="login-forgot-error" role="alert">
                {forgotError}
              </div>
            ) : null}
            <div className="login-forgot-actions login-forgot-actions--stacked">
              <button
                type="button"
                className="login-forgot-btn login-forgot-resend-btn"
                onClick={handleResend}
                disabled={forgotSubmitting || forgotCooldownSeconds > 0}
              >
                {forgotCooldownSeconds > 0
                  ? `Resend available in ${formatCooldownLabel(forgotCooldownSeconds)}`
                  : 'Resend link'}
              </button>
              <button type="button" className="login-forgot-submit-btn" onClick={onClose}>
                Back to sign in
              </button>
            </div>
          </div>
        ) : (
          <form className="login-forgot-body" onSubmit={handleSubmit} noValidate>
            <p className="login-forgot-hint">
              Enter the email you use for xSPECTRE. If an account exists, we will send reset instructions.
            </p>
            <div className="login-forgot-field">
              <input
                ref={emailInputRef}
                id="login-forgot-email"
                className="login-forgot-input"
                type="email"
                value={forgotEmail}
                onChange={(ev) => setForgotEmail(ev.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                aria-label="Email"
                required
                aria-invalid={forgotError ? 'true' : 'false'}
                aria-describedby={forgotError ? 'login-forgot-error' : undefined}
              />
            </div>
            {forgotError ? (
              <div id="login-forgot-error" className="login-forgot-error" role="alert">
                {forgotError}
              </div>
            ) : null}
            {forgotCooldownSeconds > 0 ? (
              <p className="login-forgot-hint">
                You can request another email in {formatCooldownLabel(forgotCooldownSeconds)}.
              </p>
            ) : null}
            <div className="login-forgot-actions login-forgot-actions--single">
              <button
                type="submit"
                className="login-forgot-submit-btn"
                disabled={forgotSubmitting || forgotCooldownSeconds > 0}
              >
                {forgotSubmitting ? (
                  <ButtonBusyLabel>Sending…</ButtonBusyLabel>
                ) : forgotCooldownSeconds > 0 ? (
                  `Send reset link (${formatCooldownLabel(forgotCooldownSeconds)})`
                ) : (
                  'Send reset link'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function LoginMfaDialog({ challenge, onClose, onSuccess }) {
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const recoveryInputRef = useRef(null);

  useEffect(() => {
    if (!challenge) return;
    setCode('');
    setUseRecoveryCode(false);
    setBusy(false);
    setError('');
    setMessage('');
  }, [challenge]);

  useEffect(() => {
    if (!challenge) return undefined;
    const t = window.setTimeout(() => {
      if (useRecoveryCode) recoveryInputRef.current?.focus();
      else document.getElementById('login-mfa-code')?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [useRecoveryCode, challenge]);

  if (!challenge) return null;

  const otpDigits = String(code || '').replace(/\D/g, '');
  const canSubmit = useRecoveryCode
    ? String(code || '').trim().length > 0
    : otpDigits.length === MFA_OTP_SLOT_COUNT;

  const verifyCode = async (event) => {
    event.preventDefault();
    if (busy || !canSubmit) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await verifyLoginMfaCode(challenge.loginToken, code);
      onSuccess(result);
    } catch (err) {
      setError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'mfa_verify' }));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const resendMessage = await resendLoginMfaCode(challenge.loginToken);
      setMessage(String(resendMessage || 'A new verification code was sent.'));
    } catch (err) {
      setError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'mfa_resend' }));
    } finally {
      setBusy(false);
    }
  };

  const methodLabel = String(challenge.mfaType || 'your configured method').toUpperCase();

  return (
    <div className="login-forgot-overlay" role="presentation">
      <div
        className="login-forgot-dialog login-mfa-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-mfa-title"
      >
        <div className="login-forgot-dialog-head login-mfa-dialog-head">
          <h2 id="login-mfa-title" className="login-forgot-title">Multi-factor verification</h2>
          <button
            type="button"
            className="login-forgot-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <form className="login-forgot-body login-mfa-dialog-body" onSubmit={verifyCode} noValidate>
          <p className="login-forgot-hint">
            {useRecoveryCode
              ? 'Enter one of your recovery codes to finish signing in.'
              : (
                <>
                  Enter the {MFA_OTP_SLOT_COUNT}-digit verification code sent via {methodLabel}
                  {challenge.mfaTargetMask ? ` to ${challenge.mfaTargetMask}` : ''}.
                </>
              )}
          </p>
          <div className="login-forgot-field">
            {useRecoveryCode ? (
              <input
                ref={recoveryInputRef}
                id="login-mfa-code"
                className="login-forgot-input"
                type="text"
                value={code}
                onChange={(ev) => setCode(ev.target.value)}
                placeholder="Recovery code"
                autoComplete="one-time-code"
                required
              />
            ) : (
              <MfaOtpCodeSlots
                id="login-mfa-code"
                value={code}
                onChange={setCode}
                disabled={busy}
                aria-label={`${MFA_OTP_SLOT_COUNT}-digit verification code`}
              />
            )}
          </div>
          <div className="login-mfa-secondary-actions">
            <button
              type="button"
              className="login-forgot-recovery-toggle"
              onClick={() => {
                setCode('');
                setError('');
                setUseRecoveryCode((prev) => !prev);
              }}
              disabled={busy}
            >
              {useRecoveryCode ? `Use ${MFA_OTP_SLOT_COUNT}-digit code instead` : 'Use a recovery code instead'}
            </button>
            <button
              type="button"
              className="login-forgot-btn"
              onClick={resendCode}
              disabled={busy || useRecoveryCode}
            >
              Resend code
            </button>
          </div>
          {error ? <div className="login-forgot-error" role="alert">{error}</div> : null}
          {message ? <p className="login-forgot-success">{message}</p> : null}
          <div className="login-forgot-actions login-forgot-actions--single">
            <button type="submit" className="login-forgot-submit-btn login-mfa-verify-btn" disabled={busy || !canSubmit}>
              {busy ? <ButtonBusyLabel>Verifying…</ButtonBusyLabel> : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordConfirmPage({ onBackToSignIn }) {
  const location = useLocation();
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [keyValidationLoading, setKeyValidationLoading] = useState(false);
  const [isResetKeyValid, setIsResetKeyValid] = useState(false);
  const [keyValidationError, setKeyValidationError] = useState('');

  const resetKey = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    const queryKey = String(qp.get('key') || '').trim();
    if (queryKey) return queryKey;

    const match = String(location.pathname || '').match(/^\/(?:(?:app|dashboard)\/)?reset-password\/confirm\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : '';
  }, [location.pathname, location.search]);
  const hasResetKey = Boolean(String(resetKey || '').trim());
  const showResetForm = hasResetKey && isResetKeyValid && !successMessage;
  const showManualRecovery = !hasResetKey && !successMessage;
  const showInvalidKeyRecovery = hasResetKey && !keyValidationLoading && !isResetKeyValid && !successMessage;

  useEffect(() => {
    let cancelled = false;

    if (!hasResetKey) {
      setIsResetKeyValid(false);
      setKeyValidationLoading(false);
      setKeyValidationError('');
      return undefined;
    }

    setKeyValidationLoading(true);
    setKeyValidationError('');
    setIsResetKeyValid(false);

    (async () => {
      try {
        await validatePasswordResetKey(resetKey);
        if (cancelled) return;
        setIsResetKeyValid(true);
      } catch (err) {
        if (cancelled) return;
        setIsResetKeyValid(false);
        setKeyValidationError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'password_reset_key' }));
      } finally {
        if (!cancelled) setKeyValidationLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasResetKey, resetKey]);

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const queryEmail = String(qp.get('email') || '').trim();
    if (queryEmail) setResetEmail(queryEmail);
  }, [location.search]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      setResendCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSuccessMessage('');

    if (!resetKey) {
      setError('Invalid or missing reset link key. Request a new reset email.');
      return;
    }
    if (!password1 || !password2) {
      setError('Both password fields are required.');
      return;
    }
    if (password1 !== password2) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const msg = await confirmPasswordReset({ key: resetKey, password1, password2 });
      setSuccessMessage(msg);
      setPassword1('');
      setPassword2('');
    } catch (err) {
      const normalizedError = mapLoginErrorToMessage(err?.message || String(err), { flow: 'password_reset_confirm' });
      const lower = String(normalizedError || '').toLowerCase();
      if (lower.includes('invalid') || lower.includes('expired')) {
        setIsResetKeyValid(false);
        setKeyValidationError(normalizedError);
      }
      setError(normalizedError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendNewLink = async () => {
    if (resendSubmitting || resendCooldownSeconds > 0) return;
    setResendError('');
    setResendMessage('');

    const normalizedEmail = String(resetEmail || '').trim();
    if (!normalizedEmail) {
      setResendError('Enter your account email to request a new reset link.');
      return;
    }

    setResendSubmitting(true);
    try {
      const result = await requestPasswordReset(normalizedEmail);
      const resendCooldown = Number(result?.resendCooldownSeconds || result?.retryAfterSeconds || 0);
      setResendMessage(
        String(
          result?.message ||
            'If an account exists for that email address, password reset instructions have been sent.'
        )
      );
      setResendCooldownSeconds(
        Number.isFinite(resendCooldown) && resendCooldown > 0 ? Math.ceil(resendCooldown) : 0
      );
    } catch (err) {
      const retryAfterSeconds = Number(err?.retryAfterSeconds || 0);
      if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        setResendCooldownSeconds(Math.ceil(retryAfterSeconds));
      }
      setResendError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'forgot_password' }));
    } finally {
      setResendSubmitting(false);
    }
  };

  return (
    <div className="App login-app">
      <div className="login-split">
        <div className="login-left">
          <div className="login-left-inner">
            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <h1 className="login-title">Reset password</h1>
              {showResetForm ? (
                <>
                  <p className="login-dev-footnote">
                    Enter your new password. This form confirms reset through the backend API endpoint.
                  </p>

                  <div className="login-field-group">
                    <div className="login-field">
                      <span className="login-field-icon" aria-hidden="true">
                        <IconLock />
                      </span>
                      <input
                        className="login-input-inline"
                        type={showPassword1 ? 'text' : 'password'}
                        value={password1}
                        onChange={(e) => setPassword1(e.target.value)}
                        placeholder="New password"
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword1((v) => !v)}
                        aria-label={showPassword1 ? 'Hide password' : 'Show password'}
                      >
                        {showPassword1 ? <IconEyeOff /> : <IconEye />}
                      </button>
                    </div>
                  </div>

                  <div className="login-field-group">
                    <div className="login-field">
                      <span className="login-field-icon" aria-hidden="true">
                        <IconLock />
                      </span>
                      <input
                        className="login-input-inline"
                        type={showPassword2 ? 'text' : 'password'}
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword2((v) => !v)}
                        aria-label={showPassword2 ? 'Hide password' : 'Show password'}
                      >
                        {showPassword2 ? <IconEyeOff /> : <IconEye />}
                      </button>
                    </div>
                  </div>

                  {error ? <div className="login-alert" role="alert">{error}</div> : null}

                  <button className="login-btn-primary" type="submit" disabled={submitting || !resetKey}>
                    {submitting ? <ButtonBusyLabel>Resetting…</ButtonBusyLabel> : 'Set new password'}
                  </button>
                </>
              ) : null}

              {!hasResetKey && !successMessage ? (
                <p className="login-dev-footnote">
                  This reset page requires a valid email link key. Request a new reset email below.
                </p>
              ) : null}

              {hasResetKey && keyValidationLoading && !successMessage ? (
                <p className="login-dev-footnote">Verifying reset link…</p>
              ) : null}

              {showInvalidKeyRecovery ? (
                <div className="login-reset-invalid-state" role="alert" aria-live="polite">
                  <h2 className="login-reset-invalid-title">This reset link is no longer valid</h2>
                  <p className="login-reset-invalid-copy">
                    {keyValidationError || 'This password reset link is invalid, expired, or already used.'}
                  </p>
                  <p className="login-reset-invalid-hint">
                    Request a fresh reset email below and use the newest link in your inbox.
                  </p>
                </div>
              ) : null}

              {successMessage ? (
                <div className="login-alert login-alert--success" role="status">
                  {successMessage}
                </div>
              ) : null}

              <div className="login-forgot-actions login-forgot-actions--single login-reset-back-row">
                <button type="button" className="login-forgot-submit-btn" onClick={onBackToSignIn}>
                  Back to sign in
                </button>
              </div>

              {showManualRecovery || showInvalidKeyRecovery ? (
                <div className="login-reset-resend">
                  <p className="login-forgot-hint">
                    Need a fresh reset email? Enter your account email and request a new link.
                  </p>
                  <label className="login-reset-resend-label" htmlFor="reset-resend-email">
                    Account email
                  </label>
                  <div className="login-field-group">
                    <div className="login-field">
                      <span className="login-field-icon" aria-hidden="true">
                        <IconEnvelope />
                      </span>
                      <input
                        id="reset-resend-email"
                        className="login-input-inline login-reset-resend-input"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="you@company.com"
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {resendError ? <div className="login-forgot-error" role="alert">{resendError}</div> : null}
                  {resendMessage ? <div className="login-forgot-success">{resendMessage}</div> : null}

                  <div className="login-forgot-actions login-forgot-actions--single">
                    <button
                      type="button"
                      className="login-forgot-submit-btn"
                      onClick={handleSendNewLink}
                      disabled={resendSubmitting || resendCooldownSeconds > 0}
                    >
                      {resendCooldownSeconds > 0
                        ? `Send new reset link (${formatCooldownLabel(resendCooldownSeconds)})`
                        : 'Send new reset link'}
                    </button>
                  </div>
                </div>
              ) : null}


            </form>
          </div>
        </div>

        <aside className="login-right" aria-label="Product overview">
          <header className="login-header login-header--corner">
            <div className="login-brand">
              <img
                className="login-brand-mark"
                src={loginPublicAsset('xspectre-cyber-logo.png')}
                alt="XSpectre"
                width={140}
                height={28}
                decoding="async"
              />
            </div>
          </header>
          <LoginMarketingPanel />
        </aside>
      </div>
    </div>
  );
}

function LoginMarketingPanel() {
  const publicUrl = process.env.PUBLIC_URL || '';
  const asset = (name) => (publicUrl ? `${publicUrl.replace(/\/$/, '')}/${name}` : `/${name}`);
  const heroSrc = asset('login-hero.png');
  return (
    <div className="login-right-inner">
      <div className="login-hero-wrap">
        <img className="login-hero-img" src={heroSrc} alt="" decoding="async" />
      </div>
    </div>
  );
}

function AcceptInvitationPage({ onBackToSignIn }) {
  const location = useLocation();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [keyValidationLoading, setKeyValidationLoading] = useState(false);
  const [isInviteKeyValid, setIsInviteKeyValid] = useState(false);
  const [inviteKeyValidationError, setInviteKeyValidationError] = useState('');

  const inviteKey = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    const queryKey = String(qp.get('key') || '').trim();
    if (queryKey) return queryKey;
    const match = String(location.pathname || '').match(/^\/(?:(?:app|dashboard)\/)?accept-invitation\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : '';
  }, [location.pathname, location.search]);

  const hasKey = Boolean(String(inviteKey || '').trim());
  const showInviteForm = hasKey && isInviteKeyValid && !successMessage;
  const showInvalidKeyState = hasKey && !keyValidationLoading && !isInviteKeyValid && !successMessage;

  useEffect(() => {
    let cancelled = false;

    if (!hasKey) {
      setIsInviteKeyValid(false);
      setKeyValidationLoading(false);
      setInviteKeyValidationError('');
      return undefined;
    }

    setKeyValidationLoading(true);
    setInviteKeyValidationError('');
    setIsInviteKeyValid(false);

    (async () => {
      try {
        await validateInvitationKey(inviteKey);
        if (cancelled) return;
        setIsInviteKeyValid(true);
      } catch (err) {
        if (cancelled) return;
        setIsInviteKeyValid(false);
        setInviteKeyValidationError(mapLoginErrorToMessage(err?.message || String(err), { flow: 'invite_key' }));
      } finally {
        if (!cancelled) setKeyValidationLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasKey, inviteKey]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setSuccessMessage('');

    if (!inviteKey) {
      setError('Invalid or missing invitation key. Please use the link from your invitation email.');
      return;
    }
    if (!isInviteKeyValid) {
      setError('This invitation link is no longer valid. Request a new invite from your administrator.');
      return;
    }
    if (!password || !password2) {
      setError('Both password fields are required.');
      return;
    }
    if (password !== password2) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await acceptInvitation({ key: inviteKey, name, password, password2, timezone });
      setSuccessMessage('Your account is active! Redirecting to the dashboard…');
      window.setTimeout(() => {
        const target = location.pathname.startsWith('/dashboard') ? '/dashboard/' : '/';
        window.location.assign(target);
      }, 1500);
    } catch (err) {
      const normalizedError = mapLoginErrorToMessage(err?.message || String(err), { flow: 'invite_accept' });
      const lower = String(normalizedError || '').toLowerCase();
      if (lower.includes('invalid') || lower.includes('expired') || lower.includes('already been used')) {
        setIsInviteKeyValid(false);
        setInviteKeyValidationError(normalizedError);
      }
      setError(normalizedError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App login-app">
      <div className="login-split">
        <div className="login-left">
          <div className="login-left-inner">
            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <h1 className="login-title">Accept invitation</h1>

              {!hasKey && !successMessage ? (
                <div className="login-alert" role="alert">
                  Invalid invitation link. Please use the link from your invitation email.
                </div>
              ) : null}

              {hasKey && keyValidationLoading && !successMessage ? (
                <p className="login-dev-footnote">Verifying invitation link…</p>
              ) : null}

              {showInvalidKeyState ? (
                <div className="login-reset-invalid-state" role="alert" aria-live="polite">
                  <h2 className="login-reset-invalid-title">This invitation link is no longer valid</h2>
                  <p className="login-reset-invalid-copy">
                    {inviteKeyValidationError || 'This invitation link is invalid, expired, or already used.'}
                  </p>
                  <p className="login-reset-invalid-hint">
                    Ask your administrator to send a new invitation email.
                  </p>
                </div>
              ) : null}

              {showInviteForm ? (
                <>
                  <p className="login-dev-footnote">
                    Set your name and a password to activate your xSPECTRE account.
                  </p>

                  <div className="login-field-group">
                    <label className="login-label" htmlFor="invite-name">Name</label>
                    <div className="login-field">
                      <span className="login-field-icon" aria-hidden="true">
                        <IconEnvelope />
                      </span>
                      <input
                        id="invite-name"
                        className="login-input-inline"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </div>
                  </div>

                  <div className="login-field-group">
                    <label className="login-label" htmlFor="invite-password">New password</label>
                    <div className="login-field">
                      <span className="login-field-icon" aria-hidden="true">
                        <IconLock />
                      </span>
                      <input
                        id="invite-password"
                        className="login-input-inline"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="New password"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>

                  <div className="login-field-group">
                    <label className="login-label" htmlFor="invite-password2">Confirm new password</label>
                    <div className="login-field">
                      <span className="login-field-icon" aria-hidden="true">
                        <IconLock />
                      </span>
                      <input
                        id="invite-password2"
                        className="login-input-inline"
                        type="password"
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>

                  <div className="login-field-group">
                    <label className="login-label" htmlFor="invite-timezone">Timezone</label>
                    <div className="login-field">
                      <select
                        id="invite-timezone"
                        className="login-input-inline login-input-inline--select"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        aria-label="Timezone"
                      >
                        {Intl.supportedValuesOf
                          ? Intl.supportedValuesOf('timeZone').map((tz) => (
                              <option key={tz} value={tz}>{tz}</option>
                            ))
                          : [
                              'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
                              'America/Los_Angeles', 'Europe/London', 'Europe/Paris',
                              'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
                            ].map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                      </select>
                    </div>
                  </div>

                  {error ? <div className="login-alert" role="alert">{error}</div> : null}

                  <button className="login-btn-primary" type="submit" disabled={submitting || !inviteKey || !isInviteKeyValid}>
                    {submitting ? <ButtonBusyLabel>Activating…</ButtonBusyLabel> : 'Activate account'}
                  </button>
                </>
              ) : null}

              {successMessage ? (
                <div className="login-alert login-alert--success" role="status">
                  {successMessage}
                </div>
              ) : null}

              <div className="login-forgot-actions login-forgot-actions--single login-reset-back-row">
                <button type="button" className="login-forgot-submit-btn" onClick={onBackToSignIn}>
                  Back to sign in
                </button>
              </div>
            </form>
          </div>
        </div>

        <aside className="login-right" aria-label="Product overview">
          <header className="login-header login-header--corner">
            <div className="login-brand">
              <img
                className="login-brand-mark"
                src={loginPublicAsset('xspectre-cyber-logo.png')}
                alt="XSpectre"
                width={140}
                height={28}
                decoding="async"
              />
            </div>
          </header>
          <LoginMarketingPanel />
        </aside>
      </div>
    </div>
  );
}

function isLikelyNetworkTransportFailure(message) {
  const lower = String(message || '').toLowerCase();
  return (
    lower.includes('failed to fetch') ||
    lower.includes('load failed') ||
    lower.includes('networkerror') ||
    lower.includes('network error') ||
    lower.includes('connection failed') ||
    lower.includes('connection refused') ||
    lower.includes('connection not established') ||
    lower.includes('could not connect') ||
    lower.includes('cannot connect to the api') ||
    lower.includes('econnrefused') ||
    lower.includes('enotfound') ||
    lower.includes('network request failed') ||
    lower.includes('fetch failed') ||
    lower.includes('internet disconnected') ||
    lower.includes('the network connection was lost') ||
    lower.includes('err_connection_refused') ||
    lower.includes('err_connection_reset') ||
    lower.includes('err_network_changed') ||
    lower.includes('net::err_')
  );
}

function mapLoginErrorToMessage(rawMessage, options = {}) {
  const flow = String(options?.flow || 'general').trim().toLowerCase();
  const message = String(rawMessage || '').trim() || 'Something went wrong. Please try again.';
  const lower = message.toLowerCase();
  const isDev = process.env.NODE_ENV === 'development';

  const withDevHint = (userMessage, devHint = '') => {
    if (!isDev || !devHint) return userMessage;
    return `${userMessage} ${devHint}`;
  };

  if (lower.includes('html instead of json') || lower.includes('unexpected content')) {
    return withDevHint(
      'Service configuration issue. Please try again in a moment.',
      `Dev hint: frontend reached a non-API route. Verify API base (${apiBaseForErrors}) and /api/v1/auth/token/.`
    );
  }

  if (isLikelyNetworkTransportFailure(message)) {
    return withDevHint(
      'Cannot reach the server right now. Check your connection and try again.',
      'Dev hint: ensure Django is running and the /api proxy target is correct.'
    );
  }

  if (lower.includes('insufficient_resources') || lower.includes('err_insufficient_resources')) {
    return 'Your browser is low on available network resources. Close extra tabs/apps and try again.';
  }

  if (
    lower.includes('429') ||
    lower.includes('too many requests') ||
    lower.includes('throttle') ||
    lower.includes('rate limit')
  ) {
    return 'Too many attempts. Please wait a moment, then try again.';
  }

  if (
    lower.includes('login failed (504)') ||
    lower.includes('login failed (502)') ||
    lower.includes('(504)') ||
    lower.includes('(502)') ||
    lower.includes('gateway timeout')
  ) {
    return withDevHint(
      'Sign-in is taking longer than expected. Please try again.',
      'Dev hint: check backend/proxy timeout settings and Django terminal logs.'
    );
  }

  if (flow.startsWith('mfa')) {
    if (lower.includes('invalid mfa code') || lower.includes('invalid code')) {
      return 'That verification code is invalid or expired. Request a new code and try again.';
    }
    if (lower.includes('invalid or expired login token') || lower.includes('login token')) {
      return 'Your verification session has expired. Please sign in again.';
    }
  }

  if (flow.startsWith('password_reset') || flow === 'forgot_password') {
    if (lower.includes('invalid or expired reset key') || lower.includes('invalid reset key')) {
      return 'This reset link is invalid or expired. Request a new password reset email.';
    }
    if (lower.includes('enter a valid email')) {
      return 'Enter a valid email address.';
    }
  }

  if (flow.startsWith('invite')) {
    if (lower.includes('invalid or expired invitation') || lower.includes('invalid invitation')) {
      return 'This invitation link is invalid or expired. Ask your administrator to send a new invitation.';
    }
  }

  if (
    lower.includes('login failed (401)') ||
    lower.includes('no active account') ||
    lower.includes('invalid credentials') ||
    lower.includes('authentication failed')
  ) {
    return 'Incorrect email or password. Please try again.';
  }

  if (lower.includes('registration_pending') || lower.includes('registration is still pending')) {
    return 'Your account is pending activation. Please check your invitation or activation email and complete setup first.';
  }

  if (lower.includes('permission denied') || lower.includes('forbidden') || lower.includes('(403)')) {
    return 'You do not have permission to complete this action.';
  }

  return message;
}

function formatCountdown(secondsLeft) {
  if (secondsLeft == null || Number.isNaN(Number(secondsLeft))) return '—';
  const total = Math.max(0, Math.ceil(Number(secondsLeft)));
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return minutes > 0 ? `${minutes}:${seconds}` : `${total} sec`;
}

function RejectInvitationPage({ onBackToSignIn }) {
  const location = useLocation();
  const [deleting, setDeleting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const inviteKey = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    return String(qp.get('key') || '').trim();
  }, [location.search]);

  // Deletion requires an explicit user click: email security scanners pre-render
  // emailed links, so acting on page load silently destroys accounts.
  const handleConfirmReject = async () => {
    if (!inviteKey) {
      setError('Invalid invitation key.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const { rejectInvitation } = await import('./api');
      await rejectInvitation(inviteKey);
      setConfirmed(true);
      setSuccessMessage('Your account and invitation details have been deleted successfully.');
    } catch (err) {
      const normalizedError = mapLoginErrorToMessage(err?.message || String(err), { flow: 'invite_reject' });
      setError(normalizedError);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="App login-app">
      <div className="login-split">
        <div className="login-left">
          <div className="login-left-inner">
            <div className="login-form">
              <h1 className="login-title">Delete Account</h1>

              {!confirmed && !deleting && !successMessage && (
                <div>
                  <p className="login-dev-footnote">
                    You are about to reject this invitation and permanently delete the associated
                    account. This cannot be undone.
                  </p>
                  <div className="login-actions" style={{ marginTop: '12px' }}>
                    <button
                      type="button"
                      className="login-btn"
                      onClick={handleConfirmReject}
                      disabled={!inviteKey}
                    >
                      Yes, delete my account
                    </button>
                    <button
                      type="button"
                      className="login-btn login-btn--secondary"
                      onClick={() => window.location.assign('/')}
                      style={{ marginLeft: '8px' }}
                    >
                      Cancel
                    </button>
                  </div>
                  {!inviteKey && (
                    <div className="login-alert" role="alert" style={{ marginTop: '12px' }}>
                      Invalid invitation key.
                    </div>
                  )}
                </div>
              )}

              {deleting && (
                <p className="login-dev-footnote">Processing your request…</p>
              )}

              {successMessage && (
                <div className="login-reset-success-state" role="status" aria-live="polite">
                  <p className="login-reset-success-copy">{successMessage}</p>
                  <p className="login-dev-footnote" style={{ marginTop: '10px' }}>
                    You can now safely close this browser tab.
                  </p>
                  <p className="login-dev-footnote" style={{ marginTop: '8px' }}>
                    Questions? Visit our landing page to learn more about xSPECTRE.
                  </p>
                  <div className="login-actions" style={{ marginTop: '12px' }}>
                    <a
                      className="login-btn login-btn--secondary"
                      href="https://xspectre.com.au"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Go to xspectre.com.au
                    </a>
                  </div>
                </div>
              )}

              {error && (
                <div className="login-alert" role="alert">
                  {error}
                </div>
              )}

              {error && (
                <button
                  type="button"
                  onClick={() => window.location.assign('/')}
                  className="login-btn login-btn--secondary"
                >
                  Back to Sign In
                </button>
              )}
            </div>
          </div>
        </div>
        <aside className="login-right" aria-label="Product overview">
          <header className="login-header login-header--corner">
            <div className="login-brand">
              <img
                className="login-brand-mark"
                src={loginPublicAsset('xspectre-cyber-logo.png')}
                alt="XSpectre"
                width={140}
                height={28}
                decoding="async"
              />
            </div>
          </header>
          <LoginMarketingPanel />
        </aside>
      </div>
    </div>
  );
}

function ForceChangePasswordPage({ onComplete, onSignOut }) {
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await forcePasswordChange({ new_password: newPassword, confirm_new_password: confirmPassword });
      setSuccess('Password changed successfully. Redirecting to sign in...');
      window.setTimeout(() => {
        onComplete();
      }, 650);
    } catch (err) {
      setError(err?.message || 'Failed to change password. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="App login-app" role="main" aria-labelledby="force-pw-title">
      <div className="login-split">
        <div className="login-left">
          <div className="login-left-inner">
            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <h1 id="force-pw-title" className="login-title">Password change required</h1>
              <p className="login-dev-footnote login-force-copy">
                Your account was created with a temporary password. Set a new password to continue.
              </p>

              <div className="login-field-group">
                <div className="login-field">
                  <span className="login-field-icon" aria-hidden="true">
                    <IconLock />
                  </span>
                  <input
                    id="force-new-pw"
                    className="login-input-inline"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    aria-label="New password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowNew((v) => !v)}
                    aria-label={showNew ? 'Hide new password' : 'Show new password'}
                  >
                    {showNew ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>

              <div className="login-field-group">
                <div className="login-field">
                  <span className="login-field-icon" aria-hidden="true">
                    <IconLock />
                  </span>
                  <input
                    id="force-confirm-pw"
                    className="login-input-inline"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    aria-label="Confirm new password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirm ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>

              {error ? (
                <div className="login-alert" role="alert">
                  {error}
                </div>
              ) : null}

              {success ? (
                <div className="login-alert login-alert--success" role="status" aria-live="polite">
                  {success}
                </div>
              ) : null}

              <button className="login-btn-primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <ButtonBusyLabel>{success ? 'Redirecting…' : 'Changing password…'}</ButtonBusyLabel>
                ) : (
                  'Change password'
                )}
              </button>

              <div className="login-forgot-actions login-forgot-actions--single login-force-secondary-actions">
                <button
                  type="button"
                  className="login-forgot-resend-btn"
                  onClick={onSignOut}
                  disabled={submitting}
                >
                  Sign out
                </button>
              </div>
            </form>
          </div>
        </div>

        <aside className="login-right" aria-label="Product overview">
          <header className="login-header login-header--corner">
            <div className="login-brand">
              <img
                className="login-brand-mark"
                src={loginPublicAsset('xspectre-cyber-logo.png')}
                alt="XSpectre"
                width={140}
                height={28}
                decoding="async"
              />
            </div>
          </header>
          <LoginMarketingPanel />
        </aside>
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupPendingEmail, setSignupPendingEmail] = useState('');
  const [loginMfaChallenge, setLoginMfaChallenge] = useState(null);
  const [forcePasswordChange, setForcePasswordChange] = useState(() => {
    try {
      return isForcePasswordChangePending();
    } catch {
      return false;
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isCheckingSession, setIsCheckingSession] = useState(() => !SKIP_LOGIN);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (SKIP_LOGIN) return true;
    try {
      return hasUsableAccessToken();
    } catch {
      return false;
    }
  });

  // ── Idle session state ────────────────────────────────────────────────────
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  const idleExpiredRef = useRef(false);

  const handleIdleWarn = useCallback(() => setShowIdleWarning(true), []);

  const handleIdleExpired = useCallback(
    (code) => {
      // Guard against double-firing (e.g. tick fires then BroadcastChannel echoes)
      if (idleExpiredRef.current) return;
      idleExpiredRef.current = true;

      setShowIdleWarning(false);
      const messageMap = {
        idle_timeout: 'You were signed out due to inactivity. Please sign in again.',
        session_max_exceeded: 'Your session has reached its maximum duration. Please sign in again.',
        auth_expired: 'Your session expired. Please sign in again.',
      };
      setAuthError(messageMap[code] ?? messageMap.auth_expired);
      setIsAuthenticated(false);
      setForcePasswordChange(false);
      setIsCheckingSession(false);
      setPassword('');

      // Tear down any driver.js tour overlay before the login screen mounts.
      try {
        window.dispatchEvent(new CustomEvent(TOUR_AUTH_LOGOUT_EVENT));
      } catch (_) {
        /* ignore */
      }
      forceRemoveDriverDom();

      // Clear tokens so a page-refresh doesn't silently re-authenticate.
      // logoutUser() also blacklists the refresh token on the backend.
      clearLoginTime();
      clearAuthenticatedQueryCache();
      logoutUser().catch(() => { /* best-effort — local tokens already cleared by logoutUser */ });
    },
    []
  );

  const { extendSession, warningSecondsLeft, broadcastLogout } = useIdleTimeout({
    enabled: isAuthenticated && !SKIP_LOGIN,
    onWarn: handleIdleWarn,
    onIdle: handleIdleExpired,
    idleMs: IDLE_TIMEOUT_MS,
    warnMs: IDLE_WARN_BEFORE_MS,
    sessionMaxMs: SESSION_MAX_MS,
    getSessionStart: getLoginTime,
  });

  useEffect(() => {
    if (!showIdleWarning) return;
    // null means the warning was dismissed or has not started — not "time's up".
    // Treating null as 0 used to sign users out as soon as the mouse moved.
    if (warningSecondsLeft == null) return;
    if (Number(warningSecondsLeft) <= 0) {
      handleIdleExpired('idle_timeout');
    }
  }, [showIdleWarning, warningSecondsLeft, handleIdleExpired]);

  // Reset the guard so a re-login in the same session can idle-out again.
  useEffect(() => {
    if (isAuthenticated) idleExpiredRef.current = false;
  }, [isAuthenticated]);

  // Clear any leftover MFA prompt session keys on logout / session end.
  useEffect(() => {
    if (SKIP_LOGIN) return;
    if (!isAuthenticated && !isCheckingSession) {
      clearMfaEnrollmentPromptSession();
    }
  }, [isAuthenticated, isCheckingSession]);

  useEffect(() => {
    document.title = 'XSpectre Dashboard';
  }, []);

  useEffect(() => {
    if (SKIP_LOGIN) return undefined;

    let isMounted = true;

    (async () => {
      const ok = await ensureAuthenticatedSession();
      if (!isMounted) return;
      setIsAuthenticated(ok);
      setForcePasswordChange(ok ? isForcePasswordChangePending() : false);
      setIsCheckingSession(false);
    })();

    const unsubscribe = subscribeToAuthExpired((code) => {
      const messages = {
        idle_timeout: 'You were signed out due to inactivity. Please sign in again.',
        session_max_exceeded: 'Your session has reached its maximum duration. Please sign in again.',
        auth_expired: 'Your session expired. Please sign in again.',
      };
      setAuthError(messages[code] ?? messages.auth_expired);
      clearAuthenticatedQueryCache();
      setIsAuthenticated(false);
      setForcePasswordChange(false);
      setIsCheckingSession(false);
      setPassword('');
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const containerStyle = useMemo(
    () => ({
      fontFamily: "'Poppins', sans-serif",
      background: '#0b0c10',
      minHeight: '100vh',
      color: '#fff',
    }),
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const code = params.get('sso_code');
    const ssoError = params.get('sso_error');
    if (!code && !ssoError) return;
    // Remove the code from the address bar straight away
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (ssoError) {
      setAuthError(
        ssoError === 'no_account'
          ? 'No xSPECTRE account matches that Google email. Ask your administrator for access.'
          : 'Google sign-in failed. Please try again.'
      );
      return;
    }

    (async () => {
      setIsSubmitting(true);
      try {
        const result = await loginWithSsoCode(code);
        if (result?.mfa_required === true && result?.login_token) {
          setLoginMfaChallenge({
            loginToken: result.login_token,
            mfaType: result.mfa_type,
            mfaTargetMask: result.mfa_target_mask,
          });
          return;
        }
        const requiresPasswordChange = result?.force_password_change === true;
        setForcePasswordChangePending(requiresPasswordChange);
        setForcePasswordChange(requiresPasswordChange);
        setIsAuthenticated(true);
      } catch (error) {
        setAuthError(mapLoginErrorToMessage(error?.message, { flow: 'login' }));
      } finally {
        setIsSubmitting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLoginSubmit(event) {
    event.preventDefault();
    if (isSubmitting) return;
    setAuthError('');
    setLoginMfaChallenge(null);
    setIsSubmitting(true);
    try {
      const result = await loginWithPassword(email, password);

      if (result?.mfa_required === true && result?.login_token) {
        setLoginMfaChallenge({
          loginToken: result.login_token,
          mfaType: result.mfa_type,
          mfaTargetMask: result.mfa_target_mask,
        });
        return;
      }

      if (result?.registration_pending === true) {
        setSignupPendingEmail(email);
        setSignupOpen(true);
        return;
      }

      setPassword('');
      const requiresPasswordChange = result?.force_password_change === true;
      setForcePasswordChangePending(requiresPasswordChange);
      setForcePasswordChange(requiresPasswordChange);
      setIsAuthenticated(true);
    } catch (error) {
      setAuthError(mapLoginErrorToMessage(error?.message, { flow: 'login' }));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleLogout(reasonCode = '') {
    try {
      await logoutUser();
    } catch {
      // Keep logout UX resilient even if the network request fails.
    } finally {
      // Notify other tabs only after this tab attempts backend logout.
      broadcastLogout();
      clearMfaEnrollmentPromptSession();
      clearAuthenticatedQueryCache();
      try {
        window.dispatchEvent(new CustomEvent(TOUR_AUTH_LOGOUT_EVENT));
      } catch (_) {
        /* ignore */
      }
      forceRemoveDriverDom();
      setForcePasswordChangePending(false);
      setIsAuthenticated(false);
      setForcePasswordChange(false);
      setPassword('');
      if (reasonCode === 'password_changed') {
        setAuthError('Password updated successfully. Please sign in again with your new password.');
      }
    }
  }

  if (isCheckingSession) {
    return (
      <div className="App login-app login-app--session">
        <div className="login-split">
          <div className="login-left">
            <div className="login-left-inner login-left-inner--centered">
              <p className="login-session-hint">Checking session…</p>
            </div>
          </div>
          <aside className="login-right" aria-hidden="true">
            <header className="login-header login-header--corner">
              <div className="login-brand">
                <img
                  className="login-brand-mark"
                  src={loginPublicAsset('xspectre-cyber-logo.png')}
                  alt="XSpectre"
                  width={140}
                  height={28}
                  decoding="async"
                />
              </div>
            </header>
            <LoginMarketingPanel />
          </aside>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const currentPath = String(location.pathname || '');
    const isResetConfirmPath = /^\/(?:(?:app|dashboard)\/)?reset-password\/confirm(\/.*)?$/.test(currentPath);
    const isAcceptInvitePath = /^\/(?:(?:app|dashboard)\/)?accept-invitation(\/.*)?$/.test(currentPath);
    const isRejectInvitePath = /^\/(?:(?:app|dashboard)\/)?reject-invitation(\/.*)?$/.test(currentPath);
    const signInPath = currentPath.startsWith('/app') ? '/app/dashboard' : currentPath.startsWith('/dashboard') ? '/dashboard/' : '/';
    if (isResetConfirmPath) {
      return <ResetPasswordConfirmPage onBackToSignIn={() => window.location.assign(signInPath)} />;
    }
    if (isAcceptInvitePath) {
      return <AcceptInvitationPage onBackToSignIn={() => window.location.assign(signInPath)} />;
    }
    if (isRejectInvitePath) {
      return <RejectInvitationPage onBackToSignIn={() => window.location.assign(signInPath)} />;
    }

    return (
      <div className="App login-app">
        <LoginForgotPasswordDialog
          open={forgotPasswordOpen}
          initialEmail={email}
          onClose={() => setForgotPasswordOpen(false)}
        />
        <LoginSignupDialog
          open={signupOpen}
          initialEmail={email}
          pendingEmail={signupPendingEmail}
          onClose={() => {
            setSignupOpen(false);
            setSignupPendingEmail('');
          }}
          onAuthenticated={() => {
            setSignupOpen(false);
            setSignupPendingEmail('');
            setPassword('');
            setAuthError('');
            setIsAuthenticated(true);
          }}
        />
        <LoginMfaDialog
          challenge={loginMfaChallenge}
          onClose={() => setLoginMfaChallenge(null)}
          onSuccess={(result) => {
            setLoginMfaChallenge(null);
            setPassword('');
            const requiresPasswordChange = result?.force_password_change === true;
            setForcePasswordChangePending(requiresPasswordChange);
            setForcePasswordChange(requiresPasswordChange);
            setIsAuthenticated(true);
          }}
        />
        <div className="login-split">
          <div className="login-left">
            <div className="login-left-inner">
              <form className="login-form" onSubmit={handleLoginSubmit} noValidate>
                <h1 className="login-title">Welcome</h1>
                {process.env.NODE_ENV === 'development' && (
                  <p className="login-dev-footnote" role="note">
                    Local dev: SPA on <strong>http://localhost:{process.env.PORT || '3000'}</strong>.
                    API proxy target:{' '}
                    <strong>
                      {process.env.REACT_APP_PROXY_TARGET ||
                        process.env.XSPECTRE_DEV_PROXY_TARGET ||
                        'http://127.0.0.1:8000'}
                    </strong>
                    {' '}({apiBaseForErrors}). Use a staging account when proxying to staging.
                  </p>
                )}

                <div className="login-field-group">
                  <div className="login-field">
                    <span className="login-field-icon" aria-hidden="true">
                      <IconEnvelope />
                    </span>
                    <input
                      id="email"
                      className="login-input-inline"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                      aria-label="Email"
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="login-field-group">
                  <div className="login-field">
                    <span className="login-field-icon" aria-hidden="true">
                      <IconLock />
                    </span>
                    <input
                      id="password"
                      className="login-input-inline"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      aria-label="Password"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <IconEyeOff /> : <IconEye />}
                    </button>
                  </div>
                </div>

                <div className="login-remember-row login-remember-row--end">
                  <button
                    type="button"
                    className="login-forgot-btn"
                    onClick={() => setForgotPasswordOpen(true)}
                    title="Request a password reset email"
                  >
                    Forgot password?
                  </button>
                </div>

                {authError ? (
                  <div className="login-alert" role="alert">
                    {authError}
                  </div>
                ) : null}

                <button className="login-btn-primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <ButtonBusyLabel>Signing in…</ButtonBusyLabel> : 'Sign In'}
                </button>

                <div className="login-form-divider" role="presentation">
                  <span className="login-form-divider-line" aria-hidden="true" />
                  <span className="login-form-divider-or">OR</span>
                  <span className="login-form-divider-line" aria-hidden="true" />
                </div>

                <div className="login-sso" role="group" aria-label="Single sign-on">
                  <div className="login-sso-row">
                    <button
                      type="button"
                      className="login-sso-btn"
                      onClick={() => window.location.assign(`${API_ROOT_URL}/accounts/google/login/`)}
                      disabled={isSubmitting}
                      aria-label="Continue with Google"
                      title="Continue with Google"
                    >
                      <IconGoogle />
                    </button>
                    <button
                      type="button"
                      className="login-sso-btn"
                      disabled
                      aria-label="Continue with GitHub"
                      title="GitHub sign-in is not configured."
                    >
                      <IconGithub />
                    </button>
                    <button
                      type="button"
                      className="login-sso-btn"
                      disabled
                      aria-label="Continue with Microsoft"
                      title="Microsoft sign-in is not configured."
                    >
                      <IconMicrosoft />
                    </button>
                  </div>
                </div>
              </form>

              <footer className="login-footer">
                <span className="login-footer-text">
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    className="login-footer-link"
                    onClick={() => {
                      setSignupPendingEmail('');
                      setSignupOpen(true);
                    }}
                  >
                    Sign up
                  </button>
                </span>
              </footer>
            </div>
          </div>

          <aside className="login-right" aria-label="Product overview">
            <header className="login-header login-header--corner">
              <div className="login-brand">
                <img
                  className="login-brand-mark"
                  src={loginPublicAsset('xspectre-cyber-logo.png')}
                  alt="XSpectre"
                  width={140}
                  height={28}
                  decoding="async"
                />
              </div>
            </header>
            <LoginMarketingPanel />
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="App" style={containerStyle}>
      {forcePasswordChange ? (
        <ForceChangePasswordPage
          onComplete={() => {
            setForcePasswordChangePending(false);
            setForcePasswordChange(false);
            // Explicitly end this session after password change for a predictable UX.
            handleLogout('password_changed');
          }}
          onSignOut={() => handleLogout()}
        />
      ) : null}
      {!forcePasswordChange ? (
        <Routes>
          <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/app/*" element={<DashboardLayout onLogout={handleLogout} />} />
          <Route path="/dashboard/reports/*" element={<DashboardLayout onLogout={handleLogout} />} />
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      ) : null}

      {/* ── Idle-session warning modal ─────────────────────────────────── */}
      {showIdleWarning
        ? createPortal(
        <div
          className="idle-warning-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="idle-warning-title"
          aria-describedby="idle-warning-copy"
        >
          <div className="idle-warning-dialog">
            <h2 id="idle-warning-title" className="idle-warning-title">
              Session expiring soon
            </h2>
            <p id="idle-warning-copy" className="idle-warning-copy">
              You will be signed out due to inactivity.
            </p>
            <div className="idle-warning-time" aria-live="polite">
              {formatCountdown(warningSecondsLeft)}
            </div>
            <div className="idle-warning-actions">
              <button
                type="button"
                className="idle-warning-btn idle-warning-btn--primary"
                onClick={async () => {
                  setShowIdleWarning(false);
                  await extendSession();
                }}
              >
                Stay signed in
              </button>
              <button
                type="button"
                className="idle-warning-btn idle-warning-btn--ghost"
                onClick={handleLogout}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>,
        document.body
        )
        : null}
    </div>
  );
}

export default App;
